import { useCallback, useEffect, useState } from 'react';
import {
  useContentDispatch,
  useContentSelector,
  FlowsActions,
  CollectionComponetsActions,
  ComponentsActions,
} from '@wajeez-web/stores/content';
import { useParams, useNavigate } from 'react-router-dom';
import { AiOutlineEdit } from 'react-icons/ai';
import ReactRouterPrompt from 'react-router-prompt';

import { ConfirmationModal } from '../components/ConfirmationModal';
import DynamicZones from '../components/DynamicZones';

type PageOrNavigation = {
  pageId: string | undefined;
  pageName: string | undefined;
  pageType: string | undefined;
  navigationId?: number | undefined;
  pageNameShort?: string | undefined;
  isComponent?: boolean;
};

const FlowsContentV2Page = () => {
  const { flowId, pageId } = useParams<{ flowId: string; pageId: string }>();

  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();

  const [selectedPage, setSelectedPage] = useState<
    PageOrNavigation | undefined
  >();
  const [pages, setPages] = useState<PageOrNavigation[]>([]);
  const [navigations, setNavigations] = useState<PageOrNavigation[]>();
  const [isFlowDetailsSuccess, setIsFlowDetailsSuccess] = useState(false);
  const [shouldAddedComponentsUpdate, setShouldAddedComponentsUpdate] =
    useState(false);
  const [canNavigate, setCanNavigate] = useState(true);
  const [navigationPageId, setNavigationPageId] = useState<string>();

  const flowDetails = useContentSelector(
    ({ flows }) => flows.flowDetailsByIdV2
  );
  const flowDetailsData = flowDetails.data;

  useEffect(() => {
    if (selectedPage) {
      navigate(`/flows/v2/${flowId}/pages/${selectedPage.pageId}`);
    }
  }, [pages, flowId, selectedPage]);

  useEffect(() => {
    setIsFlowDetailsSuccess(false);

    (async function () {
      const res = await contentDispatch(
        FlowsActions.getFlowDetailsByIdV2Async(Number(flowId))
      );

      if (res.meta.requestStatus === 'fulfilled') {
        setIsFlowDetailsSuccess(true);
      }
    })();
  }, [flowId]);

  useEffect(() => {
    if (isFlowDetailsSuccess) {
      const pages: PageOrNavigation[] = [];

      if (flowDetailsData?.tabs) {
        for (const tab of flowDetailsData.tabs) {
          if (tab.pages) {
            pages.push(
              ...tab.pages.map((p) => {
                return {
                  pageId: p.collectionComponentId || p.componentId,
                  pageName:
                    `${tab.name} - ` +
                    `${p.name} - ` +
                    (p.collectionComponentName || p.componentName),
                  pageType: p.collectionComponentType || p.componentType,
                  pageNameShort: `${tab.name} - ` + `${p.name}`,
                  isComponent: Boolean(p.componentId),
                };
              })
            );
          }
        }
      }

      setPages(pages.filter((p) => p.pageName));

      setNavigations(
        flowDetailsData?.typeNavigations
          ?.filter((n) => n.detailsPageId)
          .map((n) => {
            return {
              pageId: n.detailsPageId,
              pageName: n.contentEntityType,
              pageType: n.detailsPageType,
              navigationId: n.id,
              pageNameShort: n.contentEntityType,
            };
          })
      );
    }
  }, [flowDetailsData, isFlowDetailsSuccess]);

  const onPageChange = useCallback(
    (id: string | undefined) => {
      if (isFlowDetailsSuccess) {
        const foundPage = pages
          ?.concat(navigations || [])
          .find((p) => p.pageId === id);

        if (foundPage) {
          setSelectedPage(foundPage);
        } else {
          setSelectedPage(pages?.[0]);
        }
      }
    },
    [pages, navigations, isFlowDetailsSuccess]
  );

  useEffect(() => {
    onPageChange(pageId);
  }, [pageId, onPageChange]);

  useEffect(() => {
    return () => {
      contentDispatch(
        ComponentsActions.clearAllowedComponentsForCollectionComponentType()
      );

      contentDispatch(
        CollectionComponetsActions.clearCollectionComponentsById()
      );
    };
  }, []);

  const renderPages = () => {
    return pages?.map((p) => {
      return (
        <div
          key={`${p.pageId}-${p.pageName}`}
          className={`w-full py-2 px-2 font-medium rounded-[4px] transition-colors ${
            !p.isComponent
              ? `hover:bg-warmGray-100 hover:text-primary cursor-pointer ${
                  selectedPage?.pageId === p.pageId &&
                  'bg-warmGray-100 text-primary'
                }`
              : `text-warmGray-300 font-normal cursor-default`
          }`}
          onClick={() => {
            if (!p.isComponent) {
              if (shouldAddedComponentsUpdate) {
                setCanNavigate(false);

                setNavigationPageId(p.pageId);

                return;
              }

              if (p.pageId !== selectedPage?.pageId) {
                contentDispatch(
                  CollectionComponetsActions.clearCollectionComponentsById()
                );

                contentDispatch(
                  ComponentsActions.clearAllowedComponentsForCollectionComponentType()
                );

                setSelectedPage(p);
              }
            }
          }}
        >
          {!p.isComponent ? (
            p.pageNameShort
          ) : (
            <div className="w-full flex justify-between items-center">
              {p.pageNameShort}
              <div
                className="rtl:mr-2 ltr:ml-2 text-[17px] text-primary cursor-pointer"
                onClick={() => {
                  navigate(
                    `/flows/v2/${flowId}/pages/${pageId}/components/${p.pageId}/edit`
                  );
                }}
              >
                <AiOutlineEdit />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const renderNavigations = () => {
    return navigations?.map((n) => {
      return (
        <div
          key={`${n.pageId}-${n.pageName}`}
          className={`py-2 px-2 font-medium hover:bg-warmGray-100 hover:text-primary rounded-[4px] transition-colors cursor-pointer ${
            selectedPage?.navigationId === n.navigationId &&
            'bg-warmGray-100 text-primary'
          }`}
          onClick={() => {
            if (shouldAddedComponentsUpdate) {
              setCanNavigate(false);

              setNavigationPageId(n.pageId);

              return;
            }

            if (n.navigationId !== selectedPage?.navigationId) {
              contentDispatch(
                CollectionComponetsActions.clearCollectionComponentsById()
              );

              contentDispatch(
                ComponentsActions.clearAllowedComponentsForCollectionComponentType()
              );

              setSelectedPage(n);
            }
          }}
        >
          {n.pageName}
        </div>
      );
    });
  };

  return (
    <div className="w-full h-inner-screen flex justify-start items-start">
      <div className="w-[300px] h-full px-4 py-6 border-e border-warmGray-200 overflow-y-auto overflow-x-hidden">
        <h1 className="w-full pb-2 border-b text-[20px] font-bold text-center break-words">
          {flowDetailsData?.flowName}
        </h1>
        <div className="mt-6">
          <div className="px-2 text-[14px] text-warmGray-400 font-medium">
            Pages
          </div>
          <div className="mt-3">{renderPages()}</div>
        </div>
        <div className="mt-6">
          <div className="px-2 text-[14px] text-warmGray-400 font-medium">
            Navigations
          </div>
          <div className="mt-3">{renderNavigations()}</div>
        </div>
      </div>
      {pageId && pageId !== '0' && (
        <DynamicZones collectionComponentId={pageId} />
      )}
      <ReactRouterPrompt when={shouldAddedComponentsUpdate}>
        {(props) => {
          const { isActive, onConfirm, onCancel } = props;

          return (
            <ConfirmationModal
              isOpen={isActive}
              onClose={onCancel}
              onSubmit={onConfirm}
              message="Are you sure that you want to leave this page?"
            />
          );
        }}
      </ReactRouterPrompt>
      {!canNavigate && (
        <ConfirmationModal
          isOpen={!canNavigate}
          onClose={() => {
            setCanNavigate(true);
          }}
          onSubmit={() => {
            setCanNavigate(true);

            if (navigationPageId !== selectedPage?.pageId) {
              contentDispatch(
                CollectionComponetsActions.clearCollectionComponentsById()
              );

              contentDispatch(
                ComponentsActions.clearAllowedComponentsForCollectionComponentType()
              );

              setShouldAddedComponentsUpdate(false);

              onPageChange(navigationPageId);
            }
          }}
          message="Are you sure that you want to leave this page?"
        />
      )}
    </div>
  );
};

export default FlowsContentV2Page;
